import { useRef, useState } from "react";
import { Sparkles, Send, ShieldAlert } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { API } from "@/lib/api";
import { getLocale } from "@/i18n/languages";

const PROMPTS = ["What are our current DPDP gaps?", "Which controls are high risk?", "Which vendors process personal data?",
  "Which requirements are not yet effective?", "What should engineering fix?", "Create a remediation plan."];
const HEADINGS = ["Answer", "Evidence", "Regulatory Basis", "Assessment Logic", "Confidence", "Recommended Action", "Human Review"];

function Sections({ text }) {
  const parts = text.split(/^###\s+/m).filter(Boolean);
  if (parts.length < 2) return <div className="whitespace-pre-wrap text-sm text-slate-700">{text}</div>;
  return (
    <div className="space-y-2">
      {parts.map((p, i) => {
        const [head, ...rest] = p.split("\n");
        const h = head.trim();
        return (
          <div key={i} className={`rounded-md border px-3 py-2 ${h === "Human Review" ? "border-sky-200 bg-sky-50" : "border-slate-200 bg-white"}`}>
            <div className="label-caps mb-1 text-blue-700">{HEADINGS.includes(h) ? h : head}</div>
            <div className="whitespace-pre-wrap text-[13px] leading-relaxed text-slate-700">{rest.join("\n").replace(/\*\*/g, "").trim()}</div>
          </div>
        );
      })}
    </div>
  );
}

export function Copilot({ open, onOpenChange }) {
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sid = useRef(null);
  const endRef = useRef(null);

  const ask = async (q) => {
    const text = (q || input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    const update = (fn) => setMsgs((m) => { const c = [...m]; c[c.length - 1] = fn(c[c.length - 1]); return c; });
    try {
      const res = await fetch(`${API}/copilot/chat`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sid.current, lang: getLocale() }) });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Copilot unavailable");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const l of lines) {
          const d = l.replace(/^data: /, "");
          if (d === "[DONE]") continue;
          try {
            const ev = JSON.parse(d);
            if (ev.session_id) sid.current = ev.session_id;
            if (ev.delta) update((x) => ({ ...x, content: x.content + ev.delta }));
            if (ev.error) update((x) => ({ ...x, error: ev.error }));
          } catch { /* partial */ }
        }
        endRef.current?.scrollIntoView({ behavior: "smooth" });
      }
    } catch (e) {
      update((x) => ({ ...x, error: e.message }));
    }
    setBusy(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-xl" data-testid="copilot-drawer">
        <SheetHeader className="border-b px-5 py-4 text-left">
          <SheetTitle className="flex items-center gap-2 font-display"><Sparkles className="h-4 w-4 text-blue-600" />DPDP Compliance Copilot</SheetTitle>
          <SheetDescription className="text-xs">Answers use your assessment data, evidence and the regulatory library only. Not legal advice — human review required.</SheetDescription>
        </SheetHeader>
        <div className="flex-1 space-y-4 overflow-y-auto bg-slate-50 px-5 py-4">
          {msgs.length === 0 && (
            <div className="space-y-2">
              <div className="label-caps">Suggested questions</div>
              {PROMPTS.map((p, i) => (
                <button key={p} onClick={() => ask(p)} data-testid={`copilot-suggestion-${i}`} className="block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-left text-sm text-slate-700 transition-colors hover:border-blue-300 hover:bg-blue-50">{p}</button>
              ))}
            </div>
          )}
          {msgs.map((m, i) => (
            <div key={i} data-testid={`copilot-message-${m.role}-${i}`} className={m.role === "user" ? "ml-10 rounded-lg bg-blue-600 px-3 py-2 text-sm text-white" : ""}>
              {m.role === "user" ? m.content : (
                <>
                  {m.content ? <Sections text={m.content} /> : !m.error && <div className="animate-pulse text-sm text-slate-400">Retrieving context and drafting…</div>}
                  {m.error && <div data-testid="copilot-error" className="mt-2 flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"><ShieldAlert className="h-4 w-4" />{m.error}</div>}
                </>
              )}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="border-t bg-white p-3">
          <div className="flex gap-2">
            <Textarea value={input} onChange={(e) => setInput(e.target.value)} rows={2} placeholder="Ask about gaps, evidence, vendors, deadlines…" data-testid="copilot-input"
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }} className="resize-none text-sm" />
            <Button onClick={() => ask()} disabled={busy || !input.trim()} data-testid="copilot-send-button" className="self-end bg-blue-600 hover:bg-blue-700"><Send className="h-4 w-4" /></Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
